import { UnauthorizedError } from './errors.js';
import type { AuthedFetch } from './transport.js';

/**
 * The favorites half of the preferences API over an injected transport — the
 * dashboard-layout half is web-only and stays in `frontend/src/apis/preferences.ts`.
 * Favorites are per-user and server-held, so a star set on one client shows up
 * on the other. `favoriteProjectIds` are portfolio card ids, i.e. siteIds.
 */
type FavoritesResponse = { favoriteProjectIds: string[] };

const FAVORITES_BASE = '/api/me/favorites';

// `T` is asserted, not validated at runtime — callers rely on the backend's
// Zod response schemas being the single source of shape truth (the standard
// pattern across `apis/`).
async function jsonOrThrow<T>(res: Response, ctx: string): Promise<T> {
  if (res.ok) return (await res.json()) as T;
  if (res.status === 401) throw new UnauthorizedError();
  const body = await res.json().catch(() => null);
  throw new Error(`${ctx} failed: ${res.status} ${body?.error?.message ?? ''}`);
}

export function createPreferencesApi(authedFetch: AuthedFetch) {
  async function fetchFavorites(signal?: AbortSignal): Promise<string[]> {
    const res = await jsonOrThrow<FavoritesResponse>(await authedFetch(FAVORITES_BASE, { signal }), 'fetch favorites');
    return res.favoriteProjectIds;
  }

  async function updateFavorites(ids: string[]): Promise<string[]> {
    const res = await jsonOrThrow<FavoritesResponse>(
      await authedFetch(FAVORITES_BASE, { method: 'PUT', body: JSON.stringify({ favoriteProjectIds: ids }) }),
      'update favorites',
    );
    return res.favoriteProjectIds;
  }

  return { fetchFavorites, updateFavorites };
}
